import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Button,
  Text,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Textarea,
  useToast,
  Spinner,
  Center,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { FiSave, FiRefreshCw, FiSettings, FiMenu } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  getSidebarModules,
  updateSidebarModules,
} from "../services/sidebarService";
import {
  getSettings,
  updateSettings,
  resetSettings as resetSettingsAPI,
} from "../services/settingsService";

const Settings = () => {
  const navigate = useNavigate();

  // Dark mode colors
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [sidebarModules, setSidebarModules] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();

  useEffect(() => {
    fetchSettings();
    // Only fetch sidebar modules when Sidebar tab is active
    if (activeTab === 12) {
      fetchSidebarModules();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      if (response.success) {
        setSettings(response.data.settings);
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (path, value) => {
    setSettings((prev) => {
      const newSettings = { ...prev };
      const keys = path.split(".");
      let current = newSettings;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await updateSettings(settings);
      if (response.success) {
        toast({
          title: "Success",
          description: "Settings saved successfully",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await resetSettingsAPI();
      if (response.success) {
        setSettings(response.data);
        toast({
          title: "Success",
          description: "Settings reset to defaults",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error resetting settings:", error);
      toast({
        title: "Error",
        description: "Failed to reset settings",
        status: "error",
        duration: 3000,
      });
    }
  };

  const fetchSidebarModules = async () => {
    try {
      setSidebarLoading(true);
      const data = await getSidebarModules();
      setSidebarModules(data || []);
    } catch (error) {
      console.error("Error fetching sidebar modules:", error);
    } finally {
      setSidebarLoading(false);
    }
  };

  const toggleModuleActive = async (moduleId) => {
    try {
      const updatedModules = sidebarModules.map((m) =>
        m._id === moduleId ? { ...m, isActive: !m.isActive } : m,
      );
      await updateSidebarModules(updatedModules);
      setSidebarModules(updatedModules);
      toast({
        title: "Module updated",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating module",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const toggleItemActive = async (moduleId, itemIndex) => {
    try {
      const updatedModules = sidebarModules.map((m) => {
        if (m._id === moduleId) {
          const updatedItems = m.items.map((item, idx) =>
            idx === itemIndex ? { ...item, isActive: !item.isActive } : item,
          );
          return { ...m, items: updatedItems };
        }
        return m;
      });
      await updateSidebarModules(updatedModules);
      setSidebarModules(updatedModules);
      toast({
        title: "Item updated",
        status: "success",
        duration: 1500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating item",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading settings...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Container maxW="container.xl" py={2}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <HStack>
            <FiSettings size={24} />
            <Heading size="lg">System Settings</Heading>
          </HStack>
          <HStack spacing={4}>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={resetSettings}
              colorScheme="red"
              variant="outline"
            >
              Reset to Defaults
            </Button>
            <Button
              leftIcon={<FiSave />}
              onClick={saveSettings}
              colorScheme="blue"
              isLoading={saving}
              loadingText="Saving..."
            >
              Save Changes
            </Button>
          </HStack>
        </HStack>

        <Alert status="info">
          <AlertIcon />
          <Box>
            <AlertTitle>Settings Management</AlertTitle>
            <AlertDescription>
              Configure your HR system preferences, integrations, and
              organizational settings. Changes take effect immediately after
              saving.
            </AlertDescription>
          </Box>
        </Alert>

        <Tabs
          variant="enclosed"
          colorScheme="blue"
          index={activeTab}
          onChange={setActiveTab}
          isLazy
          lazyBehavior="unmount"
        >
          <TabList flexWrap="wrap">
            <Tab>Company</Tab>
            <Tab>System</Tab>
            <Tab>Attendance</Tab>
            <Tab>Leave</Tab>
            <Tab>Email</Tab>
            <Tab>Notifications</Tab>
            <Tab>Security</Tab>
            <Tab>Integrations</Tab>
            <Tab>Payroll</Tab>
            <Tab>Recruitment</Tab>
            <Tab>Appearance</Tab>
            <Tab>Features</Tab>
            <Tab>Sidebar</Tab>
          </TabList>

          <TabPanels>
            {/* Company Information */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Company Information</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Company Name</FormLabel>
                      <Input
                        value={settings?.companyName || ""}
                        onChange={(e) =>
                          updateSetting("companyName", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Company Email</FormLabel>
                      <Input
                        type="email"
                        value={settings?.companyEmail || ""}
                        onChange={(e) =>
                          updateSetting("companyEmail", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Company Phone</FormLabel>
                      <Input
                        value={settings?.companyPhone || ""}
                        onChange={(e) =>
                          updateSetting("companyPhone", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Company Website</FormLabel>
                      <Input
                        value={settings?.companyWebsite || ""}
                        onChange={(e) =>
                          updateSetting("companyWebsite", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl gridColumn="span 2">
                      <FormLabel>Company Address</FormLabel>
                      <Textarea
                        value={settings?.companyAddress || ""}
                        onChange={(e) =>
                          updateSetting("companyAddress", e.target.value)
                        }
                      />
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* System Preferences */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">System Preferences</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Timezone</FormLabel>
                      <Select
                        value={settings?.timezone || "UTC"}
                        onChange={(e) =>
                          updateSetting("timezone", e.target.value)
                        }
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">
                          Pacific Time
                        </option>
                        <option value="Europe/London">London</option>
                        <option value="Europe/Paris">Paris</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                        <option value="Asia/Kolkata">India</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Date Format</FormLabel>
                      <Select
                        value={settings?.dateFormat || "DD/MM/YYYY"}
                        onChange={(e) =>
                          updateSetting("dateFormat", e.target.value)
                        }
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Currency</FormLabel>
                      <Select
                        value={settings?.currency || "USD"}
                        onChange={(e) =>
                          updateSetting("currency", e.target.value)
                        }
                      >
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                        <option value="INR">INR (₹)</option>
                        <option value="JPY">JPY (¥)</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Language</FormLabel>
                      <Select
                        value={settings?.language || "en"}
                        onChange={(e) =>
                          updateSetting("language", e.target.value)
                        }
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="hi">Hindi</option>
                      </Select>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Attendance Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Attendance Settings</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Work Start Time</FormLabel>
                      <Input
                        type="time"
                        value={settings?.workStartTime || "09:00"}
                        onChange={(e) =>
                          updateSetting("workStartTime", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Work End Time</FormLabel>
                      <Input
                        type="time"
                        value={settings?.workEndTime || "17:00"}
                        onChange={(e) =>
                          updateSetting("workEndTime", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Break Duration (minutes)</FormLabel>
                      <NumberInput
                        value={settings?.breakDuration || 60}
                        onChange={(value) =>
                          updateSetting("breakDuration", parseInt(value))
                        }
                        min={0}
                        max={180}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Overtime Threshold (hours)</FormLabel>
                      <NumberInput
                        value={settings?.overtimeThreshold || 8}
                        onChange={(value) =>
                          updateSetting("overtimeThreshold", parseInt(value))
                        }
                        min={1}
                        max={24}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl gridColumn="span 2">
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Allow Remote Clock In</FormLabel>
                        <Switch
                          isChecked={settings?.allowRemoteClockIn || false}
                          onChange={(e) =>
                            updateSetting(
                              "allowRemoteClockIn",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Leave Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Leave Management</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Annual Leave Days</FormLabel>
                      <NumberInput
                        value={settings?.annualLeaveDays || 25}
                        onChange={(value) =>
                          updateSetting("annualLeaveDays", parseInt(value))
                        }
                        min={0}
                        max={365}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Sick Leave Days</FormLabel>
                      <NumberInput
                        value={settings?.sickLeaveDays || 10}
                        onChange={(value) =>
                          updateSetting("sickLeaveDays", parseInt(value))
                        }
                        min={0}
                        max={365}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Maternity Leave Days</FormLabel>
                      <NumberInput
                        value={settings?.maternityLeaveDays || 90}
                        onChange={(value) =>
                          updateSetting("maternityLeaveDays", parseInt(value))
                        }
                        min={0}
                        max={365}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Paternity Leave Days</FormLabel>
                      <NumberInput
                        value={settings?.paternityLeaveDays || 15}
                        onChange={(value) =>
                          updateSetting("paternityLeaveDays", parseInt(value))
                        }
                        min={0}
                        max={365}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl gridColumn="span 2">
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Leave Approval Required</FormLabel>
                        <Switch
                          isChecked={settings?.leaveApprovalRequired || true}
                          onChange={(e) =>
                            updateSetting(
                              "leaveApprovalRequired",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Email Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Email Configuration</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>SMTP Host</FormLabel>
                      <Input
                        value={settings?.smtpHost || ""}
                        onChange={(e) =>
                          updateSetting("smtpHost", e.target.value)
                        }
                        placeholder="smtp.gmail.com"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>SMTP Port</FormLabel>
                      <NumberInput
                        value={settings?.smtpPort || 587}
                        onChange={(value) =>
                          updateSetting("smtpPort", parseInt(value))
                        }
                        min={1}
                        max={65535}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>SMTP Username</FormLabel>
                      <Input
                        value={settings?.smtpUser || ""}
                        onChange={(e) =>
                          updateSetting("smtpUser", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>From Email Address</FormLabel>
                      <Input
                        type="email"
                        value={settings?.emailFrom || ""}
                        onChange={(e) =>
                          updateSetting("emailFrom", e.target.value)
                        }
                      />
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Notifications */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card bg={cardBg} borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">Email Notifications</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      {[
                        {
                          key: "newEmployee",
                          label: "New Employee Registration",
                        },
                        { key: "leaveRequests", label: "Leave Requests" },
                        { key: "attendanceAlerts", label: "Attendance Alerts" },
                        { key: "systemUpdates", label: "System Updates" },
                      ].map((item) => (
                        <HStack key={item.key} justify="space-between">
                          <FormLabel mb={0}>{item.label}</FormLabel>
                          <Switch
                            isChecked={
                              settings?.emailNotifications?.[item.key] || false
                            }
                            onChange={(e) =>
                              updateSetting(
                                `emailNotifications.${item.key}`,
                                e.target.checked,
                              )
                            }
                          />
                        </HStack>
                      ))}
                    </VStack>
                  </CardBody>
                </Card>

                <Card bg={cardBg} borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">Push Notifications</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Enable Push Notifications</FormLabel>
                        <Switch
                          isChecked={
                            settings?.pushNotifications?.enabled || false
                          }
                          onChange={(e) =>
                            updateSetting(
                              "pushNotifications.enabled",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                      {settings?.pushNotifications?.enabled && (
                        <>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Leave Reminders</FormLabel>
                            <Switch
                              isChecked={
                                settings?.pushNotifications?.leaveReminders ||
                                false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "pushNotifications.leaveReminders",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                          <HStack justify="space-between">
                            <FormLabel mb={0}>Birthday Wishes</FormLabel>
                            <Switch
                              isChecked={
                                settings?.pushNotifications?.birthdayWishes ||
                                false
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "pushNotifications.birthdayWishes",
                                  e.target.checked,
                                )
                              }
                            />
                          </HStack>
                        </>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* Security Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Security Settings</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Minimum Password Length</FormLabel>
                      <NumberInput
                        value={settings?.passwordMinLength || 8}
                        onChange={(value) =>
                          updateSetting("passwordMinLength", parseInt(value))
                        }
                        min={6}
                        max={32}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Session Timeout (minutes)</FormLabel>
                      <NumberInput
                        value={settings?.sessionTimeout || 480}
                        onChange={(value) =>
                          updateSetting("sessionTimeout", parseInt(value))
                        }
                        min={15}
                        max={1440}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Login Attempts Limit</FormLabel>
                      <NumberInput
                        value={settings?.loginAttemptsLimit || 5}
                        onChange={(value) =>
                          updateSetting("loginAttemptsLimit", parseInt(value))
                        }
                        min={1}
                        max={20}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Two-Factor Authentication</FormLabel>
                        <Switch
                          isChecked={settings?.twoFactorAuth || false}
                          onChange={(e) =>
                            updateSetting("twoFactorAuth", e.target.checked)
                          }
                        />
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Require Special Characters</FormLabel>
                        <Switch
                          isChecked={
                            settings?.passwordRequireSpecialChars || true
                          }
                          onChange={(e) =>
                            updateSetting(
                              "passwordRequireSpecialChars",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Require Numbers</FormLabel>
                        <Switch
                          isChecked={settings?.passwordRequireNumbers || true}
                          onChange={(e) =>
                            updateSetting(
                              "passwordRequireNumbers",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Integrations */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Card bg={cardBg} borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">Slack Integration</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Enable Slack Integration</FormLabel>
                        <Switch
                          isChecked={
                            settings?.integrations?.slack?.enabled || false
                          }
                          onChange={(e) =>
                            updateSetting(
                              "integrations.slack.enabled",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                      {settings?.integrations?.slack?.enabled && (
                        <>
                          <FormControl>
                            <FormLabel>Webhook URL</FormLabel>
                            <Input
                              value={
                                settings?.integrations?.slack?.webhookUrl || ""
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "integrations.slack.webhookUrl",
                                  e.target.value,
                                )
                              }
                              placeholder="https://hooks.slack.com/services/..."
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Channel</FormLabel>
                            <Input
                              value={
                                settings?.integrations?.slack?.channel || ""
                              }
                              onChange={(e) =>
                                updateSetting(
                                  "integrations.slack.channel",
                                  e.target.value,
                                )
                              }
                              placeholder="#general"
                            />
                          </FormControl>
                        </>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                <Card bg={cardBg} borderColor={borderColor}>
                  <CardHeader>
                    <Heading size="md">Microsoft Teams Integration</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Enable Teams Integration</FormLabel>
                        <Switch
                          isChecked={
                            settings?.integrations?.teams?.enabled || false
                          }
                          onChange={(e) =>
                            updateSetting(
                              "integrations.teams.enabled",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                      {settings?.integrations?.teams?.enabled && (
                        <FormControl>
                          <FormLabel>Webhook URL</FormLabel>
                          <Input
                            value={
                              settings?.integrations?.teams?.webhookUrl || ""
                            }
                            onChange={(e) =>
                              updateSetting(
                                "integrations.teams.webhookUrl",
                                e.target.value,
                              )
                            }
                            placeholder="https://outlook.office.com/webhook/..."
                          />
                        </FormControl>
                      )}
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </TabPanel>

            {/* Payroll Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Payroll Settings</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Pay Period</FormLabel>
                      <Select
                        value={
                          settings?.payrollSettings?.payPeriod || "monthly"
                        }
                        onChange={(e) =>
                          updateSetting(
                            "payrollSettings.payPeriod",
                            e.target.value,
                          )
                        }
                      >
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Payroll Cutoff Date</FormLabel>
                      <NumberInput
                        value={
                          settings?.payrollSettings?.payrollCutoffDate || 25
                        }
                        onChange={(value) =>
                          updateSetting(
                            "payrollSettings.payrollCutoffDate",
                            parseInt(value),
                          )
                        }
                        min={1}
                        max={31}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Auto Tax Calculation</FormLabel>
                        <Switch
                          isChecked={
                            settings?.payrollSettings?.taxCalculation || true
                          }
                          onChange={(e) =>
                            updateSetting(
                              "payrollSettings.taxCalculation",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Auto Process Payroll</FormLabel>
                        <Switch
                          isChecked={
                            settings?.payrollSettings?.autoProcessPayroll ||
                            false
                          }
                          onChange={(e) =>
                            updateSetting(
                              "payrollSettings.autoProcessPayroll",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Recruitment Settings */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Recruitment Settings</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Interview Rounds</FormLabel>
                      <NumberInput
                        value={
                          settings?.recruitmentSettings?.interviewRounds || 3
                        }
                        onChange={(value) =>
                          updateSetting(
                            "recruitmentSettings.interviewRounds",
                            parseInt(value),
                          )
                        }
                        min={1}
                        max={10}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Offer Expiry Days</FormLabel>
                      <NumberInput
                        value={
                          settings?.recruitmentSettings?.offerExpiryDays || 7
                        }
                        onChange={(value) =>
                          updateSetting(
                            "recruitmentSettings.offerExpiryDays",
                            parseInt(value),
                          )
                        }
                        min={1}
                        max={90}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Auto Shortlisting</FormLabel>
                        <Switch
                          isChecked={
                            settings?.recruitmentSettings?.autoShortlist ||
                            false
                          }
                          onChange={(e) =>
                            updateSetting(
                              "recruitmentSettings.autoShortlist",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel mb={0}>Background Check Required</FormLabel>
                        <Switch
                          isChecked={
                            settings?.recruitmentSettings
                              ?.backgroundCheckRequired || true
                          }
                          onChange={(e) =>
                            updateSetting(
                              "recruitmentSettings.backgroundCheckRequired",
                              e.target.checked,
                            )
                          }
                        />
                      </HStack>
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Appearance */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Appearance Settings</Heading>
                </CardHeader>
                <CardBody>
                  <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                    <FormControl>
                      <FormLabel>Primary Color</FormLabel>
                      <Input
                        type="color"
                        value={settings?.theme?.primaryColor || "#3182CE"}
                        onChange={(e) =>
                          updateSetting("theme.primaryColor", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Secondary Color</FormLabel>
                      <Input
                        type="color"
                        value={settings?.theme?.secondaryColor || "#E53E3E"}
                        onChange={(e) =>
                          updateSetting("theme.secondaryColor", e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Logo URL</FormLabel>
                      <Input
                        value={settings?.theme?.logoUrl || ""}
                        onChange={(e) =>
                          updateSetting("theme.logoUrl", e.target.value)
                        }
                        placeholder="https://example.com/logo.png"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Favicon URL</FormLabel>
                      <Input
                        value={settings?.theme?.faviconUrl || ""}
                        onChange={(e) =>
                          updateSetting("theme.faviconUrl", e.target.value)
                        }
                        placeholder="https://example.com/favicon.ico"
                      />
                    </FormControl>
                  </Grid>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Features */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Feature Flags</Heading>
                  <Text fontSize="sm" color={mutedColor}>
                    Enable or disable specific features in your HR system
                  </Text>
                </CardHeader>
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {[
                      {
                        key: "advancedReporting",
                        label: "Advanced Reporting & Analytics",
                      },
                      { key: "mobileApp", label: "Mobile App Integration" },
                      {
                        key: "apiAccess",
                        label: "API Access for Third-party Apps",
                      },
                      { key: "multiCompany", label: "Multi-Company Support" },
                      { key: "customFields", label: "Custom Fields & Forms" },
                    ].map((feature) => (
                      <HStack
                        key={feature.key}
                        justify="space-between"
                        p={3}
                        border="1px"
                        borderColor={borderColor}
                        rounded="md"
                      >
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{feature.label}</Text>
                          <Badge
                            colorScheme={
                              settings?.features?.[feature.key]
                                ? "green"
                                : "gray"
                            }
                            size="sm"
                          >
                            {settings?.features?.[feature.key]
                              ? "Enabled"
                              : "Disabled"}
                          </Badge>
                        </VStack>
                        <Switch
                          isChecked={settings?.features?.[feature.key] || false}
                          onChange={(e) =>
                            updateSetting(
                              `features.${feature.key}`,
                              e.target.checked,
                            )
                          }
                          size="lg"
                        />
                      </HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Sidebar Management */}
            <TabPanel>
              <Card bg={cardBg} borderColor={borderColor}>
                <CardHeader>
                  <Heading size="md">Sidebar Navigation Modules</Heading>
                  <Text fontSize="sm" color={mutedColor} mt={2}>
                    Toggle menu modules and items on/off
                  </Text>
                </CardHeader>
                <CardBody>
                  {sidebarLoading ? (
                    <Center py={10}>
                      <Spinner size="lg" color="teal.500" />
                    </Center>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {sidebarModules.map((module) => (
                        <Card
                          key={module._id}
                          variant="outline"
                          size="sm"
                          bg={useColorModeValue("gray.50", "gray.700")}
                        >
                          <CardBody>
                            <VStack spacing={3} align="stretch">
                              {/* Module Header */}
                              <HStack justify="space-between">
                                <HStack>
                                  <Heading size="sm">{module.heading}</Heading>
                                  <Badge
                                    colorScheme={
                                      module.isActive ? "green" : "gray"
                                    }
                                  >
                                    {module.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                </HStack>
                                <Switch
                                  isChecked={module.isActive}
                                  onChange={() =>
                                    toggleModuleActive(module._id)
                                  }
                                  colorScheme="teal"
                                  size="lg"
                                />
                              </HStack>

                              <Divider />

                              {/* Module Items */}
                              <VStack spacing={2} align="stretch" pl={4}>
                                {module.items?.map((item, idx) => (
                                  <HStack
                                    key={idx}
                                    justify="space-between"
                                    p={2}
                                    borderRadius="md"
                                    _hover={{
                                      bg: useColorModeValue(
                                        "gray.100",
                                        "gray.600",
                                      ),
                                    }}
                                  >
                                    <HStack spacing={3}>
                                      <Text fontSize="sm" fontWeight="medium">
                                        {item.label}
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        color={mutedColor}
                                        fontFamily="mono"
                                      >
                                        {item.to}
                                      </Text>
                                      <Badge
                                        size="sm"
                                        colorScheme={
                                          item.isActive ? "green" : "gray"
                                        }
                                      >
                                        {item.isActive ? "On" : "Off"}
                                      </Badge>
                                    </HStack>
                                    <Switch
                                      isChecked={item.isActive}
                                      onChange={() =>
                                        toggleItemActive(module._id, idx)
                                      }
                                      colorScheme="teal"
                                      size="sm"
                                    />
                                  </HStack>
                                ))}
                              </VStack>
                            </VStack>
                          </CardBody>
                        </Card>
                      ))}

                      {sidebarModules.length === 0 && (
                        <Alert status="info">
                          <AlertIcon />
                          No sidebar modules found. Please contact your
                          administrator.
                        </Alert>
                      )}
                    </VStack>
                  )}
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default Settings;
